import { useEffect, useRef } from "react";

/**
 * Lightweight focus trap for modal/sheet components. Usage:
 *
 *   const ref = useFocusTrap({ active: isOpen, onEscape: close });
 *   <div ref={ref}>…</div>
 *
 * Behaviour:
 *   * When `active` flips to true, remembers the currently-focused
 *     element, moves focus into the container (first focusable child,
 *     or the container itself if tabindex=-1).
 *   * While active, intercepts Tab and Shift+Tab to wrap focus
 *     within the container.
 *   * Escape closes via onEscape (caller opt-in).
 *   * On deactivate, restores focus to the original trigger so
 *     keyboard users don't lose their place.
 *
 * Not a full dialog implementation — just the focus plumbing. Callers
 * still set role="dialog" / aria-modal / aria-labelledby themselves so
 * dropdowns/popovers/sheets can share the same hook without claiming
 * dialog semantics they don't have.
 */
export default function useFocusTrap({ active, onEscape } = {}) {
  const containerRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    // Remember where focus was before the modal opened.
    restoreRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    // Move focus inside.
    const focusables = getFocusables(container);
    const first = focusables[0] || container;
    // Container itself needs tabindex so it can receive focus when
    // there's nothing focusable inside (e.g., a bare message dialog).
    if (!first.hasAttribute?.("tabindex") && first === container) {
      container.setAttribute("tabindex", "-1");
    }
    first.focus?.({ preventScroll: true });

    const onKey = (e) => {
      if (e.key === "Escape" && onEscape) {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== "Tab") return;
      const items = getFocusables(container);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && active === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };

    container.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("keydown", onKey);
      // Restore focus to the trigger if it's still in the DOM.
      const prev = restoreRef.current;
      if (prev && typeof prev.focus === "function") {
        try { prev.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    };
  }, [active, onEscape]);

  return containerRef;
}

function getFocusables(root) {
  if (!root) return [];
  const nodes = root.querySelectorAll(
    [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(", "),
  );
  // Filter: skip non-visible elements (display:none, hidden attr).
  return Array.from(nodes).filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    const rect = el.getBoundingClientRect?.();
    return !rect || rect.width > 0 || rect.height > 0;
  });
}
